import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getSpeakersApi,
  assignLocationApi,
  addUpdateSponsorQuotaApi,
  getSponsorQuotaApi,
  removeLocationApi,
  getUsersApi,
  getGuestUsersApi,
  downloadGuestUsersApi,
  getUserInfoApi,
  updateUserInfoApi,
  updateProfilePictureApi,
  syncUsersApi,
  getUserContactsApi,
  getUserSessionsApi,
  downloadUserSessionsApi,
  downloadUserContactsApi,
  toggleUserStatusApi,
  downloadUsersApi,
} from "./userSpeakerApi";
import { assignRole } from "../role/roleSlice";

export const getSpeakers = createAsyncThunk(
  "speaker/getSpeakers",
  async (params, thunkAPI) => {
    try {
      const response = await getSpeakersApi(params);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const assignLocation = createAsyncThunk(
  "speaker/assignLocation",
  async (data, thunkAPI) => {
    try {
      const response = await assignLocationApi(data);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const addUpdateSponsorQuota = createAsyncThunk(
  "speaker/addUpdateSponsorQuota",
  async (data, thunkAPI) => {
    try {
      const response = await addUpdateSponsorQuotaApi(data);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const getSponsorQuota = createAsyncThunk(
  "speaker/getSponsorQuota",
  async (sponsorId, thunkAPI) => {
    try {
      const response = await getSponsorQuotaApi(sponsorId);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const removeLocation = createAsyncThunk(
  "speaker/removeLocation",
  async (data, thunkAPI) => {
    try {
      const response = await removeLocationApi(data);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const getUsers = createAsyncThunk(
  "speaker/getUsers",
  async (params, thunkAPI) => {
    try {
      const response = await getUsersApi(params);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const getGuestUsers = createAsyncThunk(
  "speaker/getGuestUsers",
  async (params, thunkAPI) => {
    try {
      const response = await getGuestUsersApi(params);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const downloadGuestUsers = createAsyncThunk(
  "speaker/downloadGuestUsers",
  async (params, thunkAPI) => {
    try {
      const res = await downloadGuestUsersApi(params);
      const disposition = res.headers["content-disposition"];
      let fileName = "guest-users.xlsx";
      if (disposition) {
        const match = disposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const getUserInfo = createAsyncThunk(
  "speaker/getUserInfo",
  async (id, thunkAPI) => {
    try {
      const response = await getUserInfoApi(id);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const updateUserInfo = createAsyncThunk(
  "speaker/updateUserInfo",
  async ({ id, data }, thunkAPI) => {
    try {
      const response = await updateUserInfoApi(data, id);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const updateProfilePicture = createAsyncThunk(
  "speaker/updateProfilePicture",
  async ({ id, file }, thunkAPI) => {
    try {
      const response = await updateProfilePictureApi(file, id);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const syncUsers = createAsyncThunk(
  "speaker/syncUsers",
  async (locationId, thunkAPI) => {
    try {
      const response = await syncUsersApi(locationId);
      return response.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  },
  {
    condition: (_, { getState }) => {
      const { speaker } = getState();
      if (speaker.loading) return false;
    },
  }
);

export const getUserContacts = createAsyncThunk(
  "speaker/getUserContacts",
  async (params, thunkAPI) => {
    try {
      const response = await getUserContactsApi(params);
      return response.data.data;
    } catch (error) {
      return thunkAPI.rejectWithValue(error.response?.data);
    }
  }
);

export const getUserSessions = createAsyncThunk(
  "speaker/getUserSessions",
  async (params, thunkAPI) => {
    try {
      const res = await getUserSessionsApi(params);
      return res.data.data;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const toggleUserStatus = createAsyncThunk(
  "speaker/toggleUserStatus",
  async (userId, thunkAPI) => {
    try {
      const res = await toggleUserStatusApi({ userId });
      return { userId, data: res.data };
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const downloadUserSessions = createAsyncThunk(
  "speaker/downloadUserSessions",
  async (params, thunkAPI) => {
    try {
      const res = await downloadUserSessionsApi(params);

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "user-sessions.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const downloadUserContacts = createAsyncThunk(
  "speaker/downloadUserContacts",
  async (params, thunkAPI) => {
    try {
      const res = await downloadUserContactsApi(params);

      const url = window.URL.createObjectURL(new Blob([res.data]));
      const link = document.createElement("a");

      link.href = url;
      link.setAttribute("download", "user-contacts.xlsx");
      document.body.appendChild(link);
      link.click();
      link.remove();

      return true;
    } catch (err) {
      return thunkAPI.rejectWithValue(err.response?.data);
    }
  }
);

export const downloadUsers = createAsyncThunk(
  "speaker/downloadUsers",
  async (params, thunkAPI) => {
    try {
      const res = await downloadUsersApi(params);
      const disposition = res.headers["content-disposition"];
      let fileName = "users.xlsx";
      if (disposition) {
        const match =
          disposition.match(/filename="?(.+?)"?$/);
        if (match?.[1]) {
          fileName = match[1];
        }
      }

      const url = window.URL.createObjectURL(
        new Blob([res.data])
      );
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", fileName);
      document.body.appendChild(link);
      link.click();
      link.remove();
      return true;
    } catch (err) {
      return thunkAPI.rejectWithValue(
        err.response?.data
      );
    }
  }
);

const userSpeakerSlice = createSlice({
  name: "speaker",

  initialState: {
    speakers: [],
    users: [],
    guestUsers: [],
    contacts: [],
    userInfo: null,
    sponsorQuota: [],
    loading: false,
    syncLoading: false,
    error: null,
    downloadUsersLoading: false,

    total: 0,
    totalPages: 1,
    page: 1,
    guestTotal: 0,
    guestTotalPages: 1,
    guestPage: 1,

    userSessions: [],
    sessionTotal: 0,
    sessionTotalPages: 1,
    sessionPage: 1,
  },

  reducers: {},

  extraReducers: (builder) => {

    builder

      .addCase(getSpeakers.pending, (state) => {
        state.loading = true;
      })

      .addCase(getSpeakers.fulfilled, (state, action) => {
        state.loading = false;
        state.speakers = action.payload;
      })

      .addCase(getSpeakers.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload;
      })

      .addCase(assignLocation.fulfilled, (state, action) => {
        const user = state.users.find(u => u.id === action.payload.userId);
        if (user) {
          user.EventLocation = action.payload.location;
        }
      })

      .addCase(getSponsorQuota.fulfilled, (state, action) => {
        state.sponsorQuota = Array.isArray(action.payload)
          ? action.payload
          : action.payload?.items || action.payload?.quotas || [];
      })

      .addCase(removeLocation.fulfilled, (state) => { })

    builder.addCase(getUsers.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getUsers.fulfilled, (state, action) => {
      state.loading = false;

      state.users = action.payload.items || [];
      state.total = action.payload.total;
      state.totalPages = action.payload.totalPages;
      state.page = action.payload.page;
    });

    builder.addCase(getUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    });

    builder.addCase(getGuestUsers.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getGuestUsers.fulfilled, (state, action) => {
      state.loading = false;
      const payload = action.payload || {};
      state.guestUsers = Array.isArray(payload) ? payload : payload.items || payload.users || [];
      state.guestTotal = payload.total || state.guestUsers.length;
      state.guestTotalPages = payload.totalPages || 1;
      state.guestPage = Number(payload.page || 1);
    });

    builder.addCase(getGuestUsers.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })

      .addCase(getUserInfo.fulfilled, (state, action) => {
        state.userInfo = action.payload;
      })

      .addCase(updateUserInfo.fulfilled, (state, action) => {
        state.userInfo = action.payload;
      })

      .addCase(syncUsers.pending, (state) => {
        state.syncLoading = true;
      })

      .addCase(syncUsers.fulfilled, (state) => {
        state.syncLoading = false;
      })

      .addCase(syncUsers.rejected, (state, action) => {
        state.syncLoading = false;
        state.error = action.payload;
      })
    builder.addCase(getUserContacts.pending, (state) => {
      state.loading = true;
    });
    builder.addCase(assignRole.fulfilled, (state, action) => {
      const { userId, roleId } = action.payload;

      const user = state.users.find((u) => u.id === userId);

      if (user) {
        user.roleId = roleId;

        // optional: update role name instantly
        if (user.Role) {
          user.Role.id = roleId;
        }
      }
    });

    builder.addCase(getUserContacts.fulfilled, (state, action) => {
      state.loading = false;
      state.contacts = Array.isArray(action.payload)
        ? action.payload
        : action.payload?.items || action.payload?.contacts || [];
    });

    builder.addCase(getUserContacts.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })
    builder.addCase(getUserSessions.pending, (state) => {
      state.loading = true;
    });

    builder.addCase(getUserSessions.fulfilled, (state, action) => {
      state.loading = false;

      state.userSessions = action.payload.items || [];
      state.sessionTotal = action.payload.total;
      state.sessionTotalPages = action.payload.totalPages;
      state.sessionPage = Number(action.payload.page);
    });
    builder.addCase(downloadUsers.pending, (state) => {
      state.downloadUsersLoading = true;
    });

    builder.addCase(downloadUsers.fulfilled, (state) => {
      state.downloadUsersLoading = false;
    });

    builder.addCase(downloadUsers.rejected, (state) => {
      state.downloadUsersLoading = false;
    });
    builder.addCase(downloadGuestUsers.pending, (state) => {
      state.downloadUsersLoading = true;
    });

    builder.addCase(downloadGuestUsers.fulfilled, (state) => {
      state.downloadUsersLoading = false;
    });

    builder.addCase(downloadGuestUsers.rejected, (state) => {
      state.downloadUsersLoading = false;
    });
    builder.addCase(toggleUserStatus.fulfilled, (state, action) => {
      const user = state.users.find(u => u.id === action.payload.userId);
      if (user) {
        user.isActive = !user.isActive; // toggle locally
      }
    });

    builder.addCase(getUserSessions.rejected, (state, action) => {
      state.loading = false;
      state.error = action.payload;
    })


      .addCase(updateProfilePicture.fulfilled, (state, action) => {
        if (state.userInfo) {
          state.userInfo.profilePicture = action.payload.profilePicture;
        }
      });

  },
});

export default userSpeakerSlice.reducer;
